/**
 * ECKv3: independent-locksmith directory (sections/eck-locksmith-directory.liquid).
 *
 * Progressive enhancement over a server-rendered list. This file adds:
 *   - a MapLibre GL JS 4.7.1 map (assets/maplibre-gl.js, loaded by the
 *     section on approach to the viewport) with OpenFreeMap vector tiles,
 *     SVG pin markers and gold cluster bubbles;
 *   - a city / ZIP search that geocodes with Nominatim;
 *   - "Use my location" (navigator.geolocation);
 *   - haversine distance, sort and radius filter on the existing cards.
 *
 * Nominatim usage policy (https://operations.osmfoundation.org/policies/nominatim/):
 *   - requests only when the visitor submits the form, never per keystroke;
 *   - at most one request per second (a timestamp gate in `geocode`);
 *   - results cached in sessionStorage (EckUtils.safeStorage('session'));
 *   - no `email` parameter (owner decision 2026-09-30: low traffic);
 *   - the browser sends the Referer header; attribution is printed by the
 *     section under the form and by MapLibre in the map corner.
 * OpenFreeMap (https://openfreemap.org): no key; the style's own source
 * attribution (OpenFreeMap, OpenMapTiles, OpenStreetMap) stays accessible.
 *
 * ECKv3.47 (2026-10-12): MapLibre replaces Leaflet. The 3.46 map was 256 px
 * OSM rasters under a grayscale CSS filter, so it looked soft and flat gray.
 * The vector style is recolored to the ECK palette (MAP_COLORS), shaded
 * relief shows at country zoom, and nearby pins merge into count bubbles.
 *
 * Concept 1 (2026-10-09): pin colors come from CSS (currentColor), the
 * initial and "Show all" views fit the contiguous US only (a Hawaii pin no
 * longer squeezes the mainland), popups are sized to the map so they are not
 * clipped on phones, and each card name becomes a select button (phones show
 * only the selected card expanded).
 *
 * Every failure path (no result, network error, geolocation denied, MapLibre
 * missing, no WebGL, style or tile errors) leaves the list usable and writes a plain message
 * to the aria-live status line.
 */
(function () {
  'use strict';

  var NOMINATIM = 'https://nominatim.openstreetmap.org/search';
  var STYLE_URL = 'https://tiles.openfreemap.org/styles/dark';
  var SOURCE_ID = 'eck-ldir';
  var CLUSTER_MAX_ZOOM = 9;
  var US_CENTER = [-98.35, 39.5];
  // Pins hang above their point, so the top needs room for a full pin.
  var FIT_PAD = { top: 48, right: 24, bottom: 24, left: 24 };

  /*
   * ECK palette over the OpenFreeMap "dark" style: navy water against
   * charcoal land, dark-green parks, lighter roads and warm light labels.
   * Keys are the style's layer ids; a missing id is skipped, so an upstream
   * style change degrades to the stock dark look instead of breaking.
   */
  var MAP_COLORS = {
    background: { 'background-color': '#1b1d21' },
    water: { 'fill-color': '#0c1b2b' },
    waterway: { 'line-color': '#10243a' },
    landcover_wood: { 'fill-color': '#1c2520' },
    landuse_park: { 'fill-color': '#1c2620' },
    landuse_residential: { 'fill-color': '#202228' },
    building: { 'fill-color': '#26292f', 'fill-outline-color': '#30333a' },
    highway_minor: { 'line-color': '#2b2e35' },
    highway_major_inner: { 'line-color': '#3b3f47' },
    highway_major_subtle: { 'line-color': '#34373e' },
    highway_motorway_inner: { 'line-color': '#5c4a26' },
    highway_motorway_subtle: { 'line-color': '#3f3624' },
    boundary_state: { 'line-color': '#4a4e57' },
    'boundary_country_z0-4': { 'line-color': '#6b6f78' },
    'boundary_country_z5-': { 'line-color': '#6b6f78' },
    water_name: { 'text-color': '#6f93b8', 'text-halo-color': '#0c1b2b' },
    highway_name_other: { 'text-color': '#8a8780', 'text-halo-color': '#15171a' },
    highway_name_motorway: { 'text-color': '#a09a8e' },
    place_other: { 'text-color': '#a8a49c', 'text-halo-color': 'rgba(0,0,0,0.8)' },
    place_suburb: { 'text-color': '#a8a49c', 'text-halo-color': 'rgba(0,0,0,0.8)' },
    place_village: { 'text-color': '#b4b0a8', 'text-halo-color': 'rgba(0,0,0,0.8)' },
    place_town: { 'text-color': '#c4c0b8', 'text-halo-color': 'rgba(0,0,0,0.8)' },
    place_city: { 'text-color': '#ddd9d1', 'text-halo-color': 'rgba(0,0,0,0.85)' },
    place_city_large: { 'text-color': '#ece8e0', 'text-halo-color': 'rgba(0,0,0,0.85)' },
    place_state: { 'text-color': '#8f8b84', 'text-halo-color': 'rgba(0,0,0,0.8)' },
    place_country_other: { 'text-color': '#c4c0b8', 'text-halo-color': 'rgba(0,0,0,0.85)' },
    place_country_minor: { 'text-color': '#c4c0b8', 'text-halo-color': 'rgba(0,0,0,0.85)' },
    place_country_major: { 'text-color': '#d6d2ca', 'text-halo-color': 'rgba(0,0,0,0.85)' },
  };
  var ACCENT = '#f4a71a';
  var ACCENT_INK = '#1a1408';
  var CACHE_KEY = 'eck_ldir_geocode_v1';
  var MIN_GAP_MS = 1100;
  var EARTH_MI = 3958.7613;

  var PIN_SVG =
    '<svg viewBox="0 0 28 38" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
    '<path d="M14 1C6.8 1 1 6.8 1 14c0 9.7 13 23 13 23s13-13.3 13-23C27 6.8 21.2 1 14 1z" fill="currentColor" stroke="#1a1408" stroke-width="1.5"/>' +
    '<circle cx="14" cy="14" r="5" fill="#1a1408"/></svg>';
  var ORIGIN_SVG =
    '<svg viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
    '<circle cx="11" cy="11" r="9" fill="currentColor" fill-opacity="0.25" stroke="currentColor" stroke-width="2"/>' +
    '<circle cx="11" cy="11" r="4" fill="currentColor"/></svg>';
  var PHONE_MQ = '(max-width: 749px)';

  // Shared by all directory instances; a section reload never downloads twice.
  var mapAssetsPromise;
  function loadMapAssets(scriptUrl, styleUrl) {
    if (mapAssetsPromise) return mapAssetsPromise;
    mapAssetsPromise = Promise.all([
      new Promise(function (resolve, reject) {
        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = styleUrl;
        var timer = window.setTimeout(function () {
          reject(new Error('Map CSS timed out'));
        }, 15000);
        link.onload = function () {
          window.clearTimeout(timer);
          resolve();
        };
        link.onerror = function () {
          window.clearTimeout(timer);
          reject(new Error('Map CSS failed'));
        };
        document.head.appendChild(link);
      }),
      new Promise(function (resolve, reject) {
        if (window.maplibregl) {
          resolve();
          return;
        }
        var script = document.createElement('script');
        script.src = scriptUrl;
        var timer = window.setTimeout(function () {
          reject(new Error('Map script timed out'));
        }, 15000);
        script.onload = function () {
          window.clearTimeout(timer);
          resolve();
        };
        script.onerror = function () {
          window.clearTimeout(timer);
          reject(new Error('Map script failed'));
        };
        document.head.appendChild(script);
      }),
    ]);
    return mapAssetsPromise;
  }

  /* ---------- pure helpers (also exported for the node check) ---------- */

  function toRad(d) {
    return (d * Math.PI) / 180;
  }

  /** Great-circle distance in miles between two {lat, lng} points. */
  function haversineMiles(a, b) {
    var dLat = toRad(b.lat - a.lat);
    var dLng = toRad(b.lng - a.lng);
    var s =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * EARTH_MI * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  }

  /**
   * Given listings [{lat, lng, ...}], an origin and a radius in miles
   * (null / NaN = any distance), return {all, within, nearest}:
   *   all     – every listing with a numeric position, sorted by distance
   *   within  – the subset inside the radius (all of them when radius is null)
   *   nearest – the closest listing, or null
   */
  function rankByDistance(listings, origin, radiusMiles) {
    var ranked = [];
    for (var i = 0; i < listings.length; i++) {
      var l = listings[i];
      var lat = parseFloat(l.lat);
      var lng = parseFloat(l.lng);
      if (!isFinite(lat) || !isFinite(lng)) continue;
      ranked.push({ listing: l, miles: haversineMiles(origin, { lat: lat, lng: lng }) });
    }
    ranked.sort(function (x, y) {
      return x.miles - y.miles;
    });
    var limit = radiusMiles == null || !isFinite(radiusMiles) ? Infinity : radiusMiles;
    var within = ranked.filter(function (r) {
      return r.miles <= limit;
    });
    return { all: ranked, within: within, nearest: ranked.length ? ranked[0] : null };
  }

  /** True for a point inside a box around the contiguous US (lower 48). */
  function isContiguousUS(lat, lng) {
    return lat >= 24 && lat <= 50 && lng >= -125 && lng <= -66;
  }

  /**
   * Points for the overview fit: the lower-48 points when there are any,
   * otherwise every point (a directory with only Hawaii still fits).
   */
  function overviewPoints(points) {
    var main = points.filter(function (p) {
      return isContiguousUS(p[0], p[1]);
    });
    return main.length ? main : points;
  }

  /**
   * Recolor a MapLibre style object in place with MAP_COLORS and add the
   * style's shaded-relief raster (source `ne2_shaded`) just above the
   * background, faded out by zoom 7. Returns the style.
   */
  function recolorStyle(style) {
    if (!style || !Array.isArray(style.layers)) return style;
    style.layers.forEach(function (layer) {
      var colors = MAP_COLORS[layer.id];
      if (!colors) return;
      layer.paint = layer.paint || {};
      Object.keys(colors).forEach(function (k) {
        layer.paint[k] = colors[k];
      });
    });
    var hasRelief = style.layers.some(function (l) {
      return l.source === 'ne2_shaded';
    });
    if (!hasRelief && style.sources && style.sources.ne2_shaded) {
      var bg = 0;
      while (bg < style.layers.length && style.layers[bg].type === 'background') bg++;
      style.layers.splice(bg, 0, {
        id: 'eck-relief',
        type: 'raster',
        source: 'ne2_shaded',
        maxzoom: 7,
        paint: {
          'raster-opacity': ['interpolate', ['linear'], ['zoom'], 2, 0.55, 6, 0.2, 7, 0],
          'raster-saturation': -0.55,
          'raster-brightness-max': 0.42,
          'raster-contrast': 0.15,
        },
      });
    }
    return style;
  }

  /** GeoJSON FeatureCollection of the listings that have a position. */
  function listingsToGeoJSON(listings) {
    var features = [];
    listings.forEach(function (l) {
      var lat = parseFloat(l.lat);
      var lng = parseFloat(l.lng);
      if (!isFinite(lat) || !isFinite(lng)) return;
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [lng, lat] },
        properties: { handle: l.handle },
      });
    });
    return { type: 'FeatureCollection', features: features };
  }

  function formatMiles(m) {
    if (m < 10) return m.toFixed(1) + ' mi';
    return Math.round(m) + ' mi';
  }

  function normaliseQuery(q) {
    return String(q || '')
      .trim()
      .replace(/\s+/g, ' ')
      .toLowerCase();
  }

  function parseRadius(v) {
    if (v == null || v === 'any' || v === '') return null;
    var n = parseFloat(v);
    return isFinite(n) ? n : null;
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ---------- custom element ---------- */

  var isBrowser = typeof window !== 'undefined' && typeof HTMLElement !== 'undefined';

  if (isBrowser && !customElements.get('eck-locksmith-directory')) {
    class EckLocksmithDirectory extends HTMLElement {}
    (function (proto) {
      proto.connectedCallback = function () {
        if (this._mounted) return;
        this._mounted = true;
        this._generation = (this._generation || 0) + 1;
        this.events = new AbortController();
        this._clusterRequest = 0;
        this._mapLoading = false;
        this._mapReady = false;
        this._pendingHandle = null;

        this.form = this.querySelector('[data-ldir-form]');
        this.queryEl = this.querySelector('[data-ldir-query]');
        this.radiusEl = this.querySelector('[data-ldir-radius]');
        this.geoBtn = this.querySelector('[data-ldir-geo]');
        this.resetBtn = this.querySelector('[data-ldir-reset]');
        this.statusEl = this.querySelector('[data-ldir-status]');
        this.mapEl = this.querySelector('[data-ldir-map]');
        this.mapNote = this.querySelector('[data-ldir-map-note]');
        this.listEl = this.querySelector('[data-ldir-list]');
        this.countEl = this.querySelector('[data-ldir-count]');
        this.sortEl = this.querySelector('[data-ldir-sort]');
        this.emptyEl = this.querySelector('[data-ldir-empty]');

        this.listings = this.readListings();
        this.cards = {};
        var self = this;
        Array.prototype.forEach.call(this.querySelectorAll('[data-ldir-card]'), function (card) {
          self.cards[card.getAttribute('data-handle')] = card;
        });
        this.originalOrder = this.originalOrder || Array.prototype.slice.call(this.listEl.children);
        this.activeHandle = null;
        this.phoneMq = window.matchMedia ? window.matchMedia(PHONE_MQ) : null;
        this.classList.add('eck-ldir--js');
        this.buildToggles();

        this.session = window.EckUtils.safeStorage('session');
        this.lastRequestAt = 0;
        this.markers = {};
        this.originMarker = null;
        this.attribution = null;
        this.map = null;

        this.bindEvents();
        var generation = this._generation;
        this.mapObserver = window.EckUtils.revealOnIntersect(
          this.mapEl,
          function () {
            if (self._mounted && self._generation === generation) self.initMap();
          },
          { rootMargin: '150px' }
        );
      };

      proto.disconnectedCallback = function () {
        this._mounted = false;
        this._generation += 1;
        this._clusterRequest += 1;
        if (this.mapObserver) this.mapObserver.disconnect();
        if (this.events) this.events.abort();
        if (this.styleRequest) this.styleRequest.abort();
        window.clearTimeout(this.resizeTimer);
        window.clearTimeout(this.popupTimer);
        if (this.phoneMq && this.onMq) {
          if (this.phoneMq.removeEventListener) this.phoneMq.removeEventListener('change', this.onMq);
          else if (this.phoneMq.removeListener) this.phoneMq.removeListener(this.onMq);
        }
        if (this.map) {
          try {
            this.map.remove();
          } catch (e) {
            /* ignore */
          }
          this.map = null;
        }
        this._mounted = false;
      };

      proto.readListings = function () {
        var node = this.querySelector('script[data-ldir-data]');
        if (!node) return [];
        try {
          var arr = JSON.parse(node.textContent || '[]');
          return Array.isArray(arr) ? arr : [];
        } catch (e) {
          return [];
        }
      };

      proto.bindEvents = function () {
        var self = this;

        if (this.form) {
          this.form.addEventListener(
            'submit',
            function (evt) {
              evt.preventDefault();
              self.searchByQuery(self.queryEl ? self.queryEl.value : '');
            },
            { signal: self.events.signal }
          );
        }

        if (this.geoBtn) {
          if (navigator.geolocation && window.isSecureContext !== false) {
            this.geoBtn.hidden = false;
            this.geoBtn.addEventListener(
              'click',
              function () {
                self.searchByGeolocation();
              },
              { signal: self.events.signal }
            );
          }
        }

        if (this.resetBtn) {
          this.resetBtn.addEventListener(
            'click',
            function () {
              self.reset();
            },
            { signal: self.events.signal }
          );
        }

        if (this.radiusEl) {
          this.radiusEl.addEventListener(
            'change',
            function () {
              if (self.origin) self.applyOrigin(self.origin, self.originLabel);
            },
            { signal: self.events.signal }
          );
        }

        if (this.phoneMq) {
          var onMq = function () {
            self.syncExpanded();
            self.syncAttribution();
            if (self.map) self.map.resize();
          };
          this.onMq = onMq;
          if (this.phoneMq.addEventListener) this.phoneMq.addEventListener('change', onMq);
          else if (this.phoneMq.addListener) this.phoneMq.addListener(onMq);
        }

        Array.prototype.forEach.call(this.querySelectorAll('[data-ldir-toggle]'), function (btn) {
          btn.addEventListener(
            'click',
            function () {
              self.selectListing(btn.closest('[data-ldir-card]').getAttribute('data-handle'));
            },
            { signal: self.events.signal }
          );
        });

        Array.prototype.forEach.call(this.querySelectorAll('[data-ldir-show]'), function (btn) {
          btn.addEventListener(
            'click',
            function () {
              var card = btn.closest('[data-ldir-card]');
              if (card) self.focusListing(card.getAttribute('data-handle'));
            },
            { signal: self.events.signal }
          );
        });
      };

      /* ---------- cards ---------- */

      /**
       * Wrap each card name in a button. On phones the button expands the
       * card (only the selected card is expanded); everywhere it selects the
       * listing and moves the map to its pin without scrolling the page.
       */
      proto.buildToggles = function () {
        var self = this;
        Object.keys(this.cards).forEach(function (h) {
          var card = self.cards[h];
          var name = card.querySelector('.eck-ldir__name');
          var body = card.querySelector('[data-ldir-card-body]');
          if (!name || name.querySelector('.eck-ldir__toggle')) return;
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'eck-ldir__toggle';
          btn.setAttribute('data-ldir-toggle', '');
          if (body && body.id) btn.setAttribute('aria-controls', body.id);
          while (name.firstChild) btn.appendChild(name.firstChild);
          name.appendChild(btn);
        });
        this.syncExpanded();
      };

      proto.isPhone = function () {
        return !!(this.phoneMq && this.phoneMq.matches);
      };

      /** aria-expanded: every card is open on wider screens; phones open only the selected one. */
      proto.syncExpanded = function () {
        var phone = this.isPhone();
        var active = this.activeHandle;
        var self = this;
        Object.keys(this.cards).forEach(function (h) {
          var btn = self.cards[h].querySelector('[data-ldir-toggle]');
          if (btn) btn.setAttribute('aria-expanded', !phone || h === active ? 'true' : 'false');
        });
      };

      /** Select from the list: highlight, then show the pin without scrolling the page. */
      proto.selectListing = function (handle) {
        this.highlightCard(handle, false);
        if (!this._mapReady) {
          this._pendingHandle = handle;
          this.initMap();
        } else if (this.map && this.markers[handle] && this.visible[handle]) this.panToMarker(handle);
      };

      /* ---------- map ---------- */

      proto.overviewPadding = function () {
        return this.isPhone() ? { top: 44, right: 18, bottom: 30, left: 18 } : FIT_PAD;
      };

      proto.syncAttribution = function () {
        if (!this.map) return;
        if (this.attribution) this.map.removeControl(this.attribution);
        this.attribution = new window.maplibregl.AttributionControl({ compact: this.isPhone() });
        this.map.addControl(this.attribution, 'bottom-right');
        if (this.isPhone()) {
          var attribution = this.mapEl.querySelector('.maplibregl-ctrl-attrib');
          if (attribution) {
            attribution.removeAttribute('open');
            attribution.classList.remove('maplibregl-compact-show');
          }
        }
      };

      proto.initMap = function () {
        if (!this.mapEl || this.map || this._mapLoading) return;
        this._mapLoading = true;
        var self = this;
        var generation = this._generation;
        function current() {
          return self._mounted && self._generation === generation;
        }
        this.visible = {};
        this.listings.forEach(function (l) {
          self.visible[l.handle] = true;
        });
        this.styleRequest = new AbortController();
        var signal = this.styleRequest.signal;
        loadMapAssets(this.dataset.mapScript, this.dataset.mapStyle)
          .then(function () {
            if (!current()) return null;
            return fetch(STYLE_URL, { headers: { Accept: 'application/json' }, signal: signal }).then(function (res) {
              if (!res.ok) throw new Error('HTTP ' + res.status);
              return res.json();
            });
          })
          .then(function (style) {
            if (current() && style) self.buildMap(window.maplibregl, recolorStyle(style));
          })
          .catch(function () {
            if (current()) self.mapFailed('The map could not load. The list below still works.');
          });
      };

      proto.buildMap = function (ml, style) {
        var self = this;
        try {
          var bounds = [];
          this.listings.forEach(function (l) {
            var lat = parseFloat(l.lat);
            var lng = parseFloat(l.lng);
            if (isFinite(lat) && isFinite(lng)) bounds.push([lat, lng]);
          });
          // Overview = contiguous US. A Hawaii (or Alaska) pin stays on the
          // map but no longer stretches the first view across the Pacific.
          this.overviewBounds = overviewPoints(bounds);

          var opts = {
            container: this.mapEl,
            style: style,
            center: US_CENTER,
            zoom: 3,
            attributionControl: false,
            scrollZoom: false,
            dragRotate: false,
            pitchWithRotate: false,
            touchPitch: false,
            cooperativeGestures: false,
            maxZoom: 18,
          };
          if (this.overviewBounds.length) {
            opts.bounds = this.lngLatBounds(this.overviewBounds);
            opts.fitBoundsOptions = { padding: this.overviewPadding(), maxZoom: 12 };
          }
          this.map = new ml.Map(opts);
          this.syncAttribution();
          this.map.touchZoomRotate.disableRotation();
          this.map.addControl(new ml.NavigationControl({ showCompass: false }), 'top-left');

          var tileErrors = 0;
          this.map.on('error', function () {
            tileErrors += 1;
            if (tileErrors === 4) {
              self.setStatus('Map tiles are not loading right now. The list still works.', false);
            }
          });

          this.listings.forEach(function (l) {
            var lat = parseFloat(l.lat);
            var lng = parseFloat(l.lng);
            if (!isFinite(lat) || !isFinite(lng)) return;
            self.markers[l.handle] = self.makePin(ml, l, lng, lat);
          });

          this.map.on('load', function () {
            self.syncAttribution();
            if (self.origin) {
              var ranked = rankByDistance(
                self.listings,
                self.origin,
                parseRadius(self.radiusEl ? self.radiusEl.value : null)
              );
              self.updateMapForOrigin(self.origin, ranked.within);
            }
            self.addClusters();
            self.syncMarkers();
            self._mapReady = true;
            if (self._pendingHandle && self.visible[self._pendingHandle]) self.panToMarker(self._pendingHandle);
            self._pendingHandle = null;
          });
          this.map.on('render', function () {
            self.syncMarkers();
          });

          if (this.mapNote) this.mapNote.hidden = true;
          this.classList.remove('eck-ldir--map-failed');

          // Layout can shift once fonts / sticky columns settle.
          this.resizeTimer = window.setTimeout(function () {
            if (self.map) self.map.resize();
          }, 250);

          // Reveal "Show on map" buttons only when a map exists.
          Array.prototype.forEach.call(this.querySelectorAll('[data-ldir-show-item]'), function (li) {
            li.hidden = false;
          });
        } catch (e) {
          if (this.map) {
            try {
              this.map.remove();
            } catch (e2) {
              /* ignore */
            }
          }
          this.map = null;
          this.mapFailed('The map could not load. The list below still works.');
        }
      };

      /** [[lat, lng], ...] to a MapLibre LngLatBounds. */
      proto.lngLatBounds = function (points) {
        var b = new window.maplibregl.LngLatBounds();
        points.forEach(function (p) {
          b.extend([p[1], p[0]]);
        });
        return b;
      };

      proto.fitPoints = function (points, padding, maxZoom) {
        if (!this.map || !points.length) return;
        this.map.fitBounds(this.lngLatBounds(points), { padding: padding, maxZoom: maxZoom });
      };

      /**
       * One HTML marker per listing: the SVG pin is a focusable button, so
       * keyboard users can open the popup with Enter or Space.
       */
      proto.makePin = function (ml, l, lng, lat) {
        var self = this;
        var el = document.createElement('div');
        el.className = 'eck-ldir__marker';
        el.innerHTML = PIN_SVG;
        el.setAttribute('role', 'button');
        el.setAttribute('tabindex', '0');
        el.setAttribute('aria-label', (l.name || 'Listing') + ' (show details)');
        el.title = l.name || '';

        var popup = new ml.Popup(this.popupOptions()).setHTML(this.popupHtml(l));
        popup.on('open', function () {
          var box = self.mapEl.getBoundingClientRect();
          var card = popup.getElement();
          var content = card.querySelector('.maplibregl-popup-content');
          content.style.maxHeight = Math.max(140, box.height - 64) + 'px';
          var rect = card.getBoundingClientRect();
          var dx = rect.left < box.left + 12 ? rect.left - box.left - 12 : Math.max(0, rect.right - box.right + 12);
          var dy = rect.top < box.top + 12 ? rect.top - box.top - 12 : Math.max(0, rect.bottom - box.bottom + 12);
          if (dx || dy) self.map.panBy([dx, dy], { duration: 200 });
        });
        var marker = new ml.Marker({ element: el, anchor: 'bottom' })
          .setLngLat([lng, lat])
          .setPopup(popup)
          .addTo(this.map);

        el.addEventListener('click', function () {
          self.highlightCard(l.handle, false);
        });
        el.addEventListener('keydown', function (evt) {
          if (evt.key === 'Enter' || evt.key === ' ') {
            evt.preventDefault();
            marker.togglePopup();
            self.highlightCard(l.handle, false);
          }
        });
        return marker;
      };

      /** Gold count bubbles for pins that sit close together at this zoom. */
      proto.addClusters = function () {
        var map = this.map;
        var self = this;
        if (!map || map.getSource(SOURCE_ID)) return;
        map.addSource(SOURCE_ID, {
          type: 'geojson',
          data: this.visibleGeoJSON(),
          cluster: true,
          clusterRadius: 42,
          clusterMaxZoom: CLUSTER_MAX_ZOOM,
        });
        map.addLayer({
          id: 'eck-ldir-cluster-glow',
          type: 'circle',
          source: SOURCE_ID,
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': ACCENT,
            'circle-radius': ['step', ['get', 'point_count'], 23, 5, 27, 10, 31],
            'circle-opacity': 0.3,
            'circle-blur': 0.7,
          },
        });
        map.addLayer({
          id: 'eck-ldir-clusters',
          type: 'circle',
          source: SOURCE_ID,
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': '#1b1d21',
            'circle-radius': ['step', ['get', 'point_count'], 18, 5, 21, 10, 24],
            'circle-stroke-color': ACCENT,
            'circle-stroke-width': 3,
          },
        });
        map.addLayer({
          id: 'eck-ldir-cluster-count',
          type: 'symbol',
          source: SOURCE_ID,
          filter: ['has', 'point_count'],
          layout: {
            'text-field': ['get', 'point_count_abbreviated'],
            'text-font': ['Noto Sans Bold'],
            'text-size': 13,
            'text-allow-overlap': true,
          },
          paint: { 'text-color': ACCENT },
        });
        map.on('click', 'eck-ldir-clusters', function (evt) {
          var f = evt.features && evt.features[0];
          if (!f) return;
          var src = map.getSource(SOURCE_ID);
          var request = ++self._clusterRequest;
          src
            .getClusterLeaves(f.properties.cluster_id, f.properties.point_count, 0)
            .then(function (leaves) {
              if (self.map !== map || request !== self._clusterRequest || !leaves.length) return;
              var points = leaves.map(function (leaf) {
                return [leaf.geometry.coordinates[1], leaf.geometry.coordinates[0]];
              });
              self.closePopups();
              self.fitPoints(
                points,
                self.isPhone()
                  ? { top: 48, right: 40, bottom: 110, left: 40 }
                  : { top: 110, right: 40, bottom: 55, left: 40 },
                14
              );
            })
            .catch(function () {
              if (self.map === map && request === self._clusterRequest)
                self.setStatus('That group changed. Tap a cluster again.', false);
            });
          self.highlightCard(null, false);
        });
        map.on('mouseenter', 'eck-ldir-clusters', function () {
          map.getCanvas().style.cursor = 'pointer';
        });
        map.on('mouseleave', 'eck-ldir-clusters', function () {
          map.getCanvas().style.cursor = '';
        });
      };

      proto.visibleGeoJSON = function () {
        var self = this;
        return listingsToGeoJSON(
          this.listings.filter(function (l) {
            return self.visible[l.handle];
          })
        );
      };

      /**
       * Show a pin only when its listing is in the current results and is not
       * merged into a cluster bubble at this zoom. Before the cluster source
       * is ready, every visible listing shows its pin.
       */
      proto.syncMarkers = function () {
        var map = this.map;
        if (!map) return;
        var self = this;
        var single = null;
        if (map.getSource(SOURCE_ID) && map.isSourceLoaded(SOURCE_ID)) {
          single = {};
          map.querySourceFeatures(SOURCE_ID).forEach(function (f) {
            if (f.properties && !f.properties.cluster && f.properties.handle) single[f.properties.handle] = true;
          });
          // At or above the cluster limit nothing is merged; trust the list.
          if (map.getZoom() > CLUSTER_MAX_ZOOM) single = null;
        }
        Object.keys(this.markers).forEach(function (h) {
          var show = !!self.visible[h] && (!single || !!single[h]);
          var el = self.markers[h].getElement();
          if (el.hidden === !show) return;
          el.hidden = !show;
          if (!show && self.markers[h].getPopup().isOpen()) self.markers[h].togglePopup();
        });
      };

      proto.setVisible = function (handles) {
        this._clusterRequest += 1;
        this._popupRequest = (this._popupRequest || 0) + 1;
        var self = this;
        this.visible = {};
        Object.keys(this.markers).forEach(function (h) {
          self.visible[h] = !handles || !!handles[h];
        });
        var src = this.map && this.map.getSource(SOURCE_ID);
        if (src) src.setData(this.visibleGeoJSON());
        this.syncMarkers();
      };

      proto.closePopups = function () {
        var self = this;
        Object.keys(this.markers).forEach(function (h) {
          var pop = self.markers[h].getPopup();
          if (pop && pop.isOpen()) self.markers[h].togglePopup();
        });
      };

      proto.mapFailed = function (msg) {
        this.classList.add('eck-ldir--map-failed');
        if (this.mapNote) {
          this.mapNote.hidden = false;
          this.mapNote.textContent = msg;
        }
        this.setStatus(msg, true);
      };

      /**
       * Popup sizing: cap the width to the map so a phone-width map never
       * clips the popup.
       */
      proto.popupOptions = function () {
        var w = this.mapEl ? this.mapEl.clientWidth || 320 : 320;
        var bottom = [0, -36];
        return {
          maxWidth: Math.max(180, Math.min(280, w - 64)) + 'px',
          anchor: 'bottom',
          offset: {
            bottom: bottom,
            'bottom-left': bottom,
            'bottom-right': bottom,
            top: [0, 4],
            'top-left': [0, 4],
            'top-right': [0, 4],
            left: [10, -18],
            right: [-10, -18],
          },
          focusAfterOpen: false,
          closeButton: true,
        };
      };

      /**
       * Move to a pin (zooming past the cluster limit so it stands alone),
       * then open its popup once the move has ended.
       */
      proto.panToMarker = function (handle) {
        var map = this.map;
        var marker = this.markers[handle];
        if (!map || !marker) return;
        var self = this;
        var request = (this._popupRequest = (this._popupRequest || 0) + 1);
        var opened = false;
        var open = function () {
          if (opened || self.map !== map || request !== self._popupRequest) return;
          opened = true;
          self.closePopups();
          self.syncMarkers();
          marker.getPopup().setMaxWidth(self.popupOptions().maxWidth);
          marker.togglePopup();
        };
        map.once('moveend', open);
        map.easeTo({ center: marker.getLngLat(), zoom: Math.max(map.getZoom(), 11), duration: 600 });
        // A move to the current view fires no moveend; open anyway.
        window.clearTimeout(this.popupTimer);
        this.popupTimer = window.setTimeout(open, 800);
      };

      proto.popupHtml = function (l) {
        var parts = ['<strong>' + escapeHtml(l.name) + '</strong>'];
        var loc = [l.city, l.state].filter(Boolean).join(', ');
        if (loc) parts.push('<span class="eck-ldir__popup-location">' + escapeHtml(loc) + '</span>');
        if (l.phone) {
          parts.push(
            '<a class="eck-ldir__call" aria-label="Call ' +
              escapeHtml(l.name) +
              '" href="tel:' +
              escapeHtml(String(l.phone).replace(/[^\d+]/g, '')) +
              '">' +
              'Call' +
              '</a>'
          );
        }
        var lat = parseFloat(l.lat);
        var lng = parseFloat(l.lng);
        if (isFinite(lat) && isFinite(lng)) {
          parts.push(
            '<a href="https://www.google.com/maps/dir/?api=1&destination=' +
              lat +
              ',' +
              lng +
              '" target="_blank" rel="nofollow noopener">Get directions</a>'
          );
        }
        return parts.join('');
      };

      proto.focusListing = function (handle) {
        if (!this.map || !this.markers[handle]) return;
        this.highlightCard(handle, false);
        this.panToMarker(handle);
        if (window.innerWidth < 990 && this.mapEl && this.mapEl.scrollIntoView) {
          this.mapEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      };

      proto.highlightCard = function (handle, scroll) {
        var self = this;
        this.activeHandle = handle || null;
        Object.keys(this.cards).forEach(function (h) {
          self.cards[h].classList.toggle('eck-ldir__card--active', h === handle);
        });
        Object.keys(this.markers).forEach(function (h) {
          self.markers[h].getElement().classList.toggle('eck-ldir__marker--active', h === handle);
        });
        this.syncExpanded();
        var card = this.cards[handle];
        if (card && scroll && card.scrollIntoView) {
          card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      };

      /* ---------- search ---------- */

      proto.setStatus = function (text, isError) {
        if (!this.statusEl) return;
        this.statusEl.textContent = text || '';
        this.statusEl.classList.toggle('eck-ldir__status--error', !!isError);
      };

      proto.setBusy = function (busy) {
        var self = this;
        this.classList.toggle('eck-ldir--busy', !!busy);
        Array.prototype.forEach.call(this.querySelectorAll('.eck-ldir__btn'), function (b) {
          if (b === self.resetBtn) return;
          b.disabled = !!busy;
        });
      };

      proto.searchByQuery = function (raw) {
        var q = normaliseQuery(raw);
        var self = this;
        if (!q) {
          this.setStatus('Enter a city or ZIP code, or use your location.', true);
          if (this.queryEl) this.queryEl.focus();
          return;
        }
        this.setBusy(true);
        this.setStatus('Searching for ' + raw.trim() + '…', false);

        this.geocode(q).then(
          function (result) {
            self.setBusy(false);
            if (!result) {
              self.setStatus(
                'No place found for “' + raw.trim() + '”. Try a city and state, or a 5-digit ZIP code.',
                true
              );
              return;
            }
            self.applyOrigin({ lat: result.lat, lng: result.lng }, result.label);
          },
          function () {
            self.setBusy(false);
            self.setStatus('The search service did not answer. Check your connection and try again.', true);
          }
        );
      };

      proto.searchByGeolocation = function () {
        var self = this;
        if (!navigator.geolocation) {
          this.setStatus('Your browser does not share location. Enter a city or ZIP code instead.', true);
          return;
        }
        this.setBusy(true);
        this.setStatus('Finding your location…', false);
        navigator.geolocation.getCurrentPosition(
          function (pos) {
            self.setBusy(false);
            self.applyOrigin({ lat: pos.coords.latitude, lng: pos.coords.longitude }, 'your location');
          },
          function (err) {
            self.setBusy(false);
            var msg =
              err && err.code === 1
                ? 'Location access was denied. Enter a city or ZIP code instead.'
                : 'Your location could not be found. Enter a city or ZIP code instead.';
            self.setStatus(msg, true);
          },
          { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
        );
      };

      /**
       * Geocode with Nominatim. Returns a Promise of {lat, lng, label} or null.
       * Cached per normalised query in sessionStorage; one request per second.
       */
      proto.geocode = function (q) {
        var self = this;
        var cache = this.session.read(CACHE_KEY, {}) || {};
        if (Object.prototype.hasOwnProperty.call(cache, q)) {
          return Promise.resolve(cache[q]);
        }

        var now = Date.now();
        var wait = Math.max(0, this.lastRequestAt + MIN_GAP_MS - now);
        this.lastRequestAt = now + wait;

        return new Promise(function (resolve) {
          window.setTimeout(resolve, wait);
        }).then(function () {
          var params = new URLSearchParams({
            q: q,
            format: 'jsonv2',
            limit: '1',
            countrycodes: 'us',
            addressdetails: '0',
          });
          var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
          var timer = ctrl
            ? window.setTimeout(function () {
                ctrl.abort();
              }, 10000)
            : null;
          return fetch(NOMINATIM + '?' + params.toString(), {
            method: 'GET',
            headers: { Accept: 'application/json' },
            signal: ctrl ? ctrl.signal : undefined,
          })
            .then(function (res) {
              if (timer) window.clearTimeout(timer);
              if (!res.ok) throw new Error('HTTP ' + res.status);
              return res.json();
            })
            .then(function (rows) {
              var hit = Array.isArray(rows) && rows.length ? rows[0] : null;
              var out = null;
              if (hit) {
                var lat = parseFloat(hit.lat);
                var lng = parseFloat(hit.lon);
                if (isFinite(lat) && isFinite(lng)) {
                  var label = String(hit.display_name || q)
                    .split(',')
                    .slice(0, 2)
                    .join(',')
                    .trim();
                  out = { lat: lat, lng: lng, label: label };
                }
              }
              cache[q] = out;
              self.session.write(CACHE_KEY, cache);
              return out;
            });
        });
      };

      /* ---------- ranking + rendering ---------- */

      proto.applyOrigin = function (origin, label) {
        this.origin = origin;
        this.originLabel = label;
        var radius = parseRadius(this.radiusEl ? this.radiusEl.value : null);
        var ranked = rankByDistance(this.listings, origin, radius);
        var self = this;

        var shown = ranked.within;
        var visibleHandles = {};

        // Re-order and show/hide the server-rendered cards.
        var frag = document.createDocumentFragment();
        shown.forEach(function (r) {
          var card = self.cards[r.listing.handle];
          if (!card) return;
          card.hidden = false;
          visibleHandles[r.listing.handle] = true;
          var dist = card.querySelector('[data-ldir-dist]');
          if (dist) {
            dist.textContent = formatMiles(r.miles);
            dist.hidden = false;
          }
          frag.appendChild(card);
        });
        ranked.all.forEach(function (r) {
          if (visibleHandles[r.listing.handle]) return;
          var card = self.cards[r.listing.handle];
          if (!card) return;
          card.hidden = true;
          frag.appendChild(card);
        });
        // Cards with no coordinates stay at the end, still visible.
        this.originalOrder.forEach(function (card) {
          if (card.parentNode !== frag) {
            card.hidden = false;
            var dist = card.querySelector('[data-ldir-dist]');
            if (dist) dist.hidden = true;
            frag.appendChild(card);
          }
        });
        this.listEl.appendChild(frag);

        var near = label === 'your location' ? 'you' : label;
        if (this.countEl) {
          this.countEl.textContent =
            shown.length + (shown.length === 1 ? ' locksmith' : ' locksmiths') + ' near ' + near;
        }
        if (this.sortEl) this.sortEl.textContent = 'Sort: closest first';

        if (shown.length) {
          this.setStatus(
            shown.length +
              (shown.length === 1 ? ' locksmith' : ' locksmiths') +
              (radius ? ' within ' + radius + ' miles of ' : ' near ') +
              label +
              ', closest first.',
            false
          );
          if (this.emptyEl) this.emptyEl.hidden = true;
        } else {
          var msg = 'No listings within ' + radius + ' miles of ' + label + '.';
          if (ranked.nearest) {
            msg +=
              ' The closest is ' + ranked.nearest.listing.name + ', ' + formatMiles(ranked.nearest.miles) + ' away.';
          }
          msg += ' Choose a larger radius or “Any distance”.';
          this.setStatus(msg, false);
          if (this.emptyEl) {
            this.emptyEl.textContent = msg;
            this.emptyEl.hidden = false;
          }
        }

        if (this.resetBtn) this.resetBtn.hidden = false;
        this.updateMapForOrigin(origin, shown);
        // Select the nearest result so phones show one expanded card.
        this.highlightCard(shown.length ? shown[0].listing.handle : null, false);
      };

      proto.updateMapForOrigin = function (origin, shown) {
        var ml = window.maplibregl;
        if (!this.map || !ml) return;

        if (!this.originMarker) {
          var el = document.createElement('div');
          el.className = 'eck-ldir__marker eck-ldir__marker--origin';
          el.innerHTML = ORIGIN_SVG;
          el.setAttribute('aria-hidden', 'true');
          this.originMarker = new ml.Marker({ element: el, anchor: 'center' })
            .setLngLat([origin.lng, origin.lat])
            .addTo(this.map);
        } else {
          this.originMarker.setLngLat([origin.lng, origin.lat]);
        }

        var handles = {};
        shown.forEach(function (r) {
          handles[r.listing.handle] = true;
        });
        this.closePopups();
        this.setVisible(handles);

        var pts = [[origin.lat, origin.lng]];
        shown.forEach(function (r) {
          pts.push([parseFloat(r.listing.lat), parseFloat(r.listing.lng)]);
        });
        if (pts.length > 1) {
          this.fitPoints(pts, FIT_PAD, 11);
        } else {
          this.map.easeTo({ center: [origin.lng, origin.lat], zoom: 8 });
        }
      };

      proto.reset = function () {
        this._pendingHandle = null;
        var self = this;
        this.origin = null;
        this.originLabel = null;
        var frag = document.createDocumentFragment();
        this.originalOrder.forEach(function (card) {
          card.hidden = false;
          card.classList.remove('eck-ldir__card--active');
          var dist = card.querySelector('[data-ldir-dist]');
          if (dist) {
            dist.hidden = true;
            dist.textContent = '';
          }
          frag.appendChild(card);
        });
        this.listEl.appendChild(frag);
        if (this.countEl) {
          var n = this.originalOrder.length;
          this.countEl.textContent = n + (n === 1 ? ' locksmith' : ' locksmiths');
        }
        if (this.sortEl) this.sortEl.textContent = 'Sort: directory order';
        this.highlightCard(null, false);
        if (this.emptyEl) this.emptyEl.hidden = true;
        if (this.queryEl) this.queryEl.value = '';
        if (this.resetBtn) this.resetBtn.hidden = true;
        this.setStatus('Showing all locksmiths.', false);

        if (this.map) {
          if (this.originMarker) {
            this.originMarker.remove();
            this.originMarker = null;
          }
          this.closePopups();
          this.setVisible(null);
          if (this.overviewBounds && this.overviewBounds.length) {
            this.fitPoints(this.overviewBounds, this.overviewPadding(), 12);
          }
        }
      };
    })(EckLocksmithDirectory.prototype);

    customElements.define('eck-locksmith-directory', EckLocksmithDirectory);
  }

  /* Exported for the offline node check (not used by the theme). */
  var api = {
    haversineMiles: haversineMiles,
    rankByDistance: rankByDistance,
    formatMiles: formatMiles,
    normaliseQuery: normaliseQuery,
    parseRadius: parseRadius,
    isContiguousUS: isContiguousUS,
    overviewPoints: overviewPoints,
    recolorStyle: recolorStyle,
    listingsToGeoJSON: listingsToGeoJSON,
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
